import React from "react"
import { Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faTableColumns,
  faRightLeft,
  faColumns,
  faExpand,
  faCompress,
} from "@fortawesome/free-solid-svg-icons"
import { useQueryConsoleLayoutStore } from "stores/queryConsoleLayoutStore"

/**
 * Layout-control instrument cluster.
 *
 * Three icon-only buttons that adjust the workspace layout:
 *   1. Show/hide the variables (DataTree) panel.
 *   2. Toggle the Console+Output split between vertical and horizontal.
 *   3. Swap which of Console/Output appears first.
 *
 * Visually a single grouped unit; tooltips describe the resulting action.
 * Active/non-default state is shown by a 2px underbar in the brand accent.
 */
const QueryConsoleLayoutControls: React.FC = () => {
  const viewMode = useQueryConsoleLayoutStore((s) => s.viewMode)
  const dataTreeVisible = useQueryConsoleLayoutStore((s) => s.dataTreeVisible)
  const consoleOutputDirection = useQueryConsoleLayoutStore(
    (s) => s.consoleOutputDirection
  )
  const consoleOutputOrder = useQueryConsoleLayoutStore(
    (s) => s.consoleOutputOrder
  )
  const isMaximized = useQueryConsoleLayoutStore((s) => s.isMaximized)
  const toggleDataTree = useQueryConsoleLayoutStore((s) => s.toggleDataTree)
  const toggleDirection = useQueryConsoleLayoutStore((s) => s.toggleDirection)
  const toggleOrder = useQueryConsoleLayoutStore((s) => s.toggleOrder)
  const toggleMaximized = useQueryConsoleLayoutStore((s) => s.toggleMaximized)

  const isHorizontal = consoleOutputDirection === "horizontal"
  const isOutputFirst = consoleOutputOrder === "output-first"
  // Direction and order toggles apply wherever there is an editor/output (or
  // builder/output) split. Transform and Saved Queries are single-pane
  // workspaces with no second pane, so they're hidden there.
  const showSplitControls = viewMode === "builder" || viewMode === "console"

  return (
    <div className="qc-layout-cluster" role="group" aria-label="Layout">
      <Tooltip
        title={dataTreeVisible ? "Hide variables panel" : "Show variables panel"}
        placement="bottom"
      >
        <button
          type="button"
          className="qc-layout-cluster__btn"
          aria-label="Toggle variables panel"
          aria-pressed={!dataTreeVisible}
          data-active={!dataTreeVisible}
          onClick={toggleDataTree}
        >
          <FontAwesomeIcon icon={faColumns} />
        </button>
      </Tooltip>
      {showSplitControls && (
        <>
          <Tooltip
            title={
              isHorizontal
                ? "Stack panels vertically"
                : "Place panels side by side"
            }
            placement="bottom"
          >
            <button
              type="button"
              className="qc-layout-cluster__btn"
              aria-label="Toggle layout direction"
              aria-pressed={isHorizontal}
              data-active={isHorizontal}
              onClick={toggleDirection}
            >
              <FontAwesomeIcon
                icon={faTableColumns}
                style={
                  isHorizontal ? undefined : { transform: "rotate(90deg)" }
                }
              />
            </button>
          </Tooltip>
          <Tooltip title="Swap panel positions" placement="bottom">
            <button
              type="button"
              className="qc-layout-cluster__btn"
              aria-label="Swap panel positions"
              aria-pressed={isOutputFirst}
              data-active={isOutputFirst}
              onClick={toggleOrder}
            >
              <FontAwesomeIcon icon={faRightLeft} />
            </button>
          </Tooltip>
        </>
      )}
      <Tooltip
        title={
          isMaximized
            ? "Exit full screen (Esc)"
            : "Expand to full screen"
        }
        placement="bottom"
      >
        <button
          type="button"
          className="qc-layout-cluster__btn"
          aria-label={isMaximized ? "Exit full screen" : "Enter full screen"}
          aria-pressed={isMaximized}
          data-active={isMaximized}
          onClick={toggleMaximized}
        >
          <FontAwesomeIcon icon={isMaximized ? faCompress : faExpand} />
        </button>
      </Tooltip>
    </div>
  )
}

export default QueryConsoleLayoutControls
